// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CachedBomDeltaListProperty } from '@/audit/api/types';
import { Stack, Typography } from '@mui/material';

interface Props {
  /**
   * The cached BOM delta list property containing added/removed/updated BOM items
   */
  property: CachedBomDeltaListProperty;
}

/**
 * Component for displaying BOM delta list changes in audit logs.
 * Shows added, removed, and updated BOM items with their quantities and part details.
 *
 * Follows the same pattern as other delta list components like entity and billing delta lists.
 */
export function AuditCachedBomDeltaListLabel(props: Props) {
  const added = props.property.newValue?.added || [];
  const removed = props.property.newValue?.removed || [];
  const updated = props.property.newValue?.updated || [];

  // Don't render if all arrays are empty
  if (added.length === 0 && removed.length === 0 && updated.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>

      {added.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Added:
          </Typography>
          {added.map((item) => (
            <Typography key={item.key} variant="caption" sx={{ marginLeft: 2 }}>
              {item.sku ? `${item.sku} (${item.name})` : item.name} - qty:{' '}
              {item.qty}
            </Typography>
          ))}
        </Stack>
      )}

      {removed.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Removed:
          </Typography>
          {removed.map((item) => (
            <Typography key={item.key} variant="caption" sx={{ marginLeft: 2 }}>
              {item.sku ? `${item.sku} (${item.name})` : item.name} - qty:{' '}
              {item.qty}
            </Typography>
          ))}
        </Stack>
      )}

      {updated.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Updated:
          </Typography>
          {updated.map((updateItem) => (
            <Typography
              key={`updated-${updateItem.new.id}`}
              variant="caption"
              sx={{ marginLeft: 2 }}
            >
              {updateItem.new.sku
                ? `${updateItem.new.sku} (${updateItem.new.name})`
                : updateItem.new.name}{' '}
              - qty: {updateItem.old.qty} → {updateItem.new.qty}
            </Typography>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
